import type { LocationData } from '@ikegps/core-api';
import {
	CollectionModel,
	CollectionModelFieldAnchor,
	CollectionModelFieldLocation,
	CollectionModelFieldVector,
	IkeGeodesic,
} from '@ikegps/core-shared';
import { omit } from 'lodash';
import { action, observable, makeObservable } from 'mobx';
import { AppStore } from '../../store';
import type { PoleAccuracy } from './map.point/map.point';

export interface PointCollectionLineInfo {
	field: CollectionModelFieldVector | CollectionModelFieldAnchor;
	target: LocationData;
	source: LocationData;
}

class Cacheable<T extends {}> {
	@observable private cache: {
		[key: string]: T;
	} = {};

	constructor() {
		makeObservable(this);
	}

	@action push(key: string, value: T, ttlMs?: number) {
		this.cache[key] = value;
	}

	@action flush() {
		this.cache = {};
	}

	@action drop(key: string) {
		this.cache = omit(this.cache, key);
	}

	read(key: string): T | null {
		return this.cache[key] || null;
	}

	view(transformer: (k: string) => T | null) {
		return (key: string) => {
			const existing = this.read(key);

			if (existing) {
				return existing;
			}

			const result = transformer(key);

			if (result) {
				this.push(key, result);
			}

			return this.read(key);
		};
	}

	all() {
		return Object.freeze({ ...this.cache });
	}
}

export class TopologicalPointCollectionCache {
	private static vectors = new Cacheable<PointCollectionLineInfo[]>();
	private static locations = new Cacheable<LocationData>();
	private static poleAccuracy = new Cacheable<PoleAccuracy>();

	static getCollectionVectors(collectionOrRevision: CollectionModel | string): PointCollectionLineInfo[] {
		// Handle both CollectionModel and string (revision) inputs
		const collection =
			typeof collectionOrRevision === 'string' ? AppStore.Collection.get(collectionOrRevision) : collectionOrRevision;

		if (!collection) {
			return [];
		}

		// Check cache first
		const cached = TopologicalPointCollectionCache.vectors.read(collection.revision);
		if (cached) {
			return cached;
		}

		const source = collection.location;
		if (!source) {
			return [];
		}

		const output = [];

		collection.forEachField((field) => {
			if (!field.value) {
				return;
			}

			if (CollectionModelFieldVector.isVectorField(field)) {
				const value = field.value;
				const target = IkeGeodesic.projectLocation(source, value.bearing, value.distance);
				output.push({ field, source, target });
			}

			if (CollectionModelFieldAnchor.isAnchorField(field)) {
				const { distance, bearing } = field.distanceBearingPitch(source);

				if (distance === 0) {
					return;
				}

				const target = IkeGeodesic.projectLocation(source, bearing, distance);
				if (target && target.latitude && target.longitude) {
					output.push({ field, source, target });
				}
			}
		});

		// Cache the result
		TopologicalPointCollectionCache.vectors.push(collection.revision, output);

		return output;
	}

	static getCollectionLocation(collectionOrRevision: CollectionModel | string): LocationData | null {
		// Handle both CollectionModel and string (revision) inputs
		const collection =
			typeof collectionOrRevision === 'string' ? AppStore.Collection.get(collectionOrRevision) : collectionOrRevision;

		if (!collection) {
			return null;
		}

		// Check cache first
		const cached = TopologicalPointCollectionCache.locations.read(collection.revision);
		if (cached) {
			return cached;
		}

		// Get location and cache it
		const location = collection.location;
		if (location) {
			TopologicalPointCollectionCache.locations.push(collection.revision, location);
		}

		return location;
	}

	static getCollectionPoleAccuracy(collectionOrRevision: CollectionModel | string): PoleAccuracy {
		// Handle both CollectionModel and string (revision) inputs
		const collection =
			typeof collectionOrRevision === 'string' ? AppStore.Collection.get(collectionOrRevision) : collectionOrRevision;

		if (!collection) {
			return 'default';
		}

		// Check cache first
		const cached = TopologicalPointCollectionCache.poleAccuracy.read(collection.revision);
		if (cached) {
			return cached;
		}

		// Calculate pole accuracy
		const field = collection.locationModelField;
		let poleAccuracy: PoleAccuracy = 'default';

		if (CollectionModelFieldLocation.isLocationField(field)) {
			switch (field.provider) {
				case 'truesize':
					poleAccuracy = 'low';
					break;
				case 'gps':
					let accuracy = field.value.accuracy;

					if (field.raw.fix === 'rtkfixed') {
						poleAccuracy = accuracy > 0.1 ? 'rtk_float' : 'rtk_fixed';
					} else if (field.raw.fix === 'rtkfloat') {
						poleAccuracy = accuracy > 1 ? 'low' : 'rtk_float';
					} else {
						poleAccuracy = 'low';
					}
					break;
				default:
					poleAccuracy = field.provider;
			}
		} else {
			poleAccuracy = 'low';
		}

		// Cache the result
		TopologicalPointCollectionCache.poleAccuracy.push(collection.revision, poleAccuracy);

		return poleAccuracy;
	}

	static dropCollectionVectors(revision: string) {
		TopologicalPointCollectionCache.vectors.drop(revision);
	}

	static dropCollectionLocations(revision: string) {
		TopologicalPointCollectionCache.dropCollectionVectors(revision);
		TopologicalPointCollectionCache.locations.drop(revision);
		TopologicalPointCollectionCache.dropCollectionPoleAccuracy(revision);
	}

	static dropCollectionPoleAccuracy(revision: string) {
		TopologicalPointCollectionCache.poleAccuracy.drop(revision);
	}

	static addVector(sourceRevision: string, field: CollectionModelFieldVector) {
		const source = TopologicalPointCollectionCache.getCollectionLocation(sourceRevision);

		const {
			value: { bearing, distance },
		} = field;

		const target = IkeGeodesic.projectLocation(source, bearing, distance);

		const vectors = TopologicalPointCollectionCache.getCollectionVectors(sourceRevision);
		vectors.push({ field, source, target });
		TopologicalPointCollectionCache.vectors.push(sourceRevision, vectors);
	}

	static flushVectors() {
		this.vectors.flush();
	}
}
